// "Four rules every ClearMakes product follows": a plain numbered list on blue, and
// beside it one picture that acts out the rule you are on. The list moves on by itself
// every few seconds while it is on screen; clicking a rule stays on it for a while.
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { MousePointer2 } from "lucide-react";
import { integrationByKey } from "@/data/site";
import { EASE, Rise, useStill, useStory } from "./motion";

const RULES = [
  { title: "AI should finish the job", body: "Agents carry the work through to a result, instead of handing it back with suggestions." },
  { title: "Meet people where they work", body: "Our products connect to the tools your team already uses. Nothing to move, no new habit." },
  { title: "People stay in charge", body: "You decide what runs on its own and what waits for you. Access can be taken back in one click." },
  { title: "One account, every product", body: "Inbox, Agents and ClearMeet open with the same login, so the next product is one click away." },
];

const ROTATE_MS = 5200;
const card = "rounded-2xl border border-[hsl(216_30%_88%)] bg-background p-5 shadow-[0_30px_60px_-30px_hsl(222_70%_15%/0.55)]";

/* 01: a job goes from waiting to done, with the result */
const FinishJob = () => {
  const { ref, step } = useStory(4, 1000, 2200);
  const status = ["Waiting", "Working", "Working", "Done"][step];
  return (
    <div ref={ref} className={card}>
      <p className="text-[13px] text-muted-foreground">Task</p>
      <p className="mt-1 text-[19px] font-semibold tracking-[-0.01em] text-foreground">Answer Northwind's pricing question</p>
      <div className="mt-5 flex items-center gap-3">
        <span className={`rounded-full px-3 py-1 text-[13px] font-semibold transition-colors duration-300 ${step === 3 ? "bg-foreground text-background" : "bg-[hsl(216_40%_95%)] text-foreground"}`}>{status}</span>
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[hsl(216_40%_93%)]">
          <motion.div className="h-full rounded-full bg-foreground" animate={{ width: ["8%", "35%", "75%", "100%"][step] }} transition={{ duration: 0.8, ease: EASE }} />
        </div>
      </div>
      <div className={`mt-5 rounded-xl border border-border p-4 transition-all duration-500 ${step === 3 ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}>
        <p className="text-[13px] text-muted-foreground">Result</p>
        <p className="mt-1 text-[16px] leading-snug text-foreground">Reply sent with the price list: 40 seats at $18, first 30 days free.</p>
      </div>
    </div>
  );
};

/* 02: the tools you already use, joining one workspace */
const APPS = ["gmail", "outlook", "slack", "teams", "notion", "hubspot"];
const WhereTheyWork = () => {
  const { ref, step } = useStory(APPS.length + 1, 480, 2400);
  const R = 118;
  return (
    <div ref={ref} className={`${card} relative`}>
      <svg viewBox="-160 -150 320 300" className="mx-auto block h-[280px] w-full max-w-[360px]" aria-hidden>
        {APPS.map((k, i) => {
          const a = (i / APPS.length) * Math.PI * 2 - Math.PI / 2;
          return (
            <motion.line
              key={k}
              x1={0} y1={0} x2={Math.cos(a) * R} y2={Math.sin(a) * R}
              stroke="hsl(0 0% 9%)" strokeWidth={1.4} strokeDasharray="4 5"
              initial={false}
              animate={{ pathLength: step > i ? 1 : 0, opacity: step > i ? 1 : 0 }}
              transition={{ duration: 0.4, ease: EASE }}
            />
          );
        })}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-background shadow-[0_10px_30px_-12px_hsl(0_0%_0%/0.35)]">
          <img src="/logo-mark-256.png" alt="" className="h-9 w-9 object-contain" />
        </span>
      </div>
      {APPS.map((k, i) => {
        const a = (i / APPS.length) * Math.PI * 2 - Math.PI / 2;
        const app = integrationByKey(k)!;
        return (
          <span
            key={k}
            className={`absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-xl border bg-background transition-all duration-300 ${step > i ? "border-foreground" : "border-border"}`}
            style={{ left: `calc(50% + ${Math.cos(a) * R * 0.94}px)`, top: `calc(50% + ${Math.sin(a) * R * 0.94}px)` }}
          >
            <img src={app.logo} alt={app.name} className="h-6 w-6 object-contain" />
          </span>
        );
      })}
    </div>
  );
};

/* 03: a draft waits; a person approves it */
const InCharge = () => {
  const { ref, step } = useStory(4, 1000, 2400);
  return (
    <div ref={ref} className={`${card} relative overflow-hidden`}>
      <p className="text-[13px] text-muted-foreground">Draft reply to Maya R.</p>
      <p className="mt-2 text-[16px] leading-relaxed text-foreground">Hi Maya, your order left us on Tuesday and should reach you by Friday.</p>
      <div className="relative mt-5 flex items-center gap-2">
        <motion.span
          animate={{ scale: step === 2 ? 0.95 : 1 }}
          className={`rounded-lg px-4 py-2 text-[14px] font-semibold transition-colors duration-300 ${step >= 3 ? "bg-[hsl(216_40%_94%)] text-muted-foreground" : "bg-foreground text-background"}`}
        >
          {step >= 3 ? "Sent" : "Approve and send"}
        </motion.span>
        <span className="rounded-lg border border-border px-4 py-2 text-[14px] font-medium text-foreground">Edit</span>
        <motion.span
          className="absolute"
          initial={false}
          animate={{ x: step >= 1 ? 70 : 220, y: step >= 1 ? 14 : 60, opacity: step >= 1 && step < 3 ? 1 : 0 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <MousePointer2 className="h-5 w-5 fill-foreground text-background" />
        </motion.span>
      </div>
      <p className={`mt-4 text-[14px] text-muted-foreground transition-opacity duration-300 ${step >= 3 ? "opacity-100" : "opacity-0"}`}>Sent by you at 10:42. Nothing leaves without your OK.</p>
    </div>
  );
};

/* 04: one sign-in opens every product */
const PRODUCTS = [
  { logo: "/brand/inbox-mark-256.png", name: "ClearMakes Inbox", line: "Workflows and answers" },
  { logo: "/brand/agents-mark-256.png", name: "ClearMakes Agents", line: "Agents for team and website" },
  { logo: "/brand/meet-mark-256.png", name: "ClearMeet", line: "Answers in every meeting" },
];
const OneAccount = () => {
  const { ref, step } = useStory(4, 700, 2600);
  return (
    <div ref={ref} className={card}>
      <div className="flex items-center gap-3 border-b border-border pb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-[14px] font-bold text-background">MR</span>
        <div>
          <p className="text-[15px] font-semibold text-foreground">Maya Reed</p>
          <p className="text-[13px] text-muted-foreground">Signed in to ClearMakes</p>
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {PRODUCTS.map((p, i) => (
          <li key={p.name} className={`flex items-center gap-3 rounded-xl border border-border px-3 py-3 transition-all duration-500 ${step > i ? "translate-x-0 opacity-100" : "translate-x-3 opacity-0"}`}>
            <img src={p.logo} alt="" className="h-7 w-7 object-contain" />
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-semibold text-foreground">{p.name}</span>
              <span className="block text-[13px] text-muted-foreground">{p.line}</span>
            </span>
            <span className="rounded-lg bg-foreground px-3 py-1.5 text-[13px] font-semibold text-background">Open</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PICTURES = [FinishJob, WhereTheyWork, InCharge, OneAccount];

export const Rules = () => {
  const [i, setI] = useState(0);
  const [held, setHeld] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const inView = useInView(box, { margin: "-120px" });
  const still = useStill();

  useEffect(() => {
    if (still || !inView || held) return;
    const t = setTimeout(() => setI((v) => (v + 1) % RULES.length), ROTATE_MS);
    return () => clearTimeout(t);
  }, [i, inView, held, still]);

  const Picture = PICTURES[i];

  return (
    <div ref={box} className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
      <div>
        <Rise>
          <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[hsl(0_0%_100%/0.75)]">How we build</p>
          <h2 id="beliefs-h" className="mt-4 max-w-xl text-[2.2rem] font-semibold leading-[1.06] tracking-[-0.035em] sm:text-[3.2rem]">
            Four rules every ClearMakes product follows.
          </h2>
        </Rise>
        <ol className="mt-10 border-t border-[hsl(0_0%_100%/0.22)]">
          {RULES.map((r, n) => (
            <li key={r.title} className="border-b border-[hsl(0_0%_100%/0.22)]">
              <button
                type="button"
                aria-expanded={n === i}
                onClick={() => {
                  setI(n);
                  setHeld(true);
                  setTimeout(() => setHeld(false), 14000);
                }}
                className="relative flex w-full items-baseline gap-5 py-5 text-left"
              >
                <span className={`font-mono text-[14px] transition-colors ${n === i ? "text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_100%/0.5)]"}`}>0{n + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-[1.35rem] font-semibold tracking-[-0.02em] transition-colors sm:text-[1.55rem] ${n === i ? "text-[hsl(0_0%_100%)]" : "text-[hsl(0_0%_100%/0.55)]"}`}>{r.title}</span>
                  <AnimatePresence initial={false}>
                    {n === i && (
                      <motion.span
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="block overflow-hidden"
                      >
                        <span className="block pt-2 text-[16px] leading-relaxed text-[hsl(0_0%_100%/0.8)]">{r.body}</span>
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
                {n === i && !still && !held && inView && (
                  <motion.span
                    key={`bar-${i}`}
                    aria-hidden
                    className="absolute -bottom-px left-0 h-[2px] bg-[hsl(0_0%_100%)]"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                  />
                )}
              </button>
            </li>
          ))}
        </ol>
      </div>
      <div aria-hidden className="relative mx-auto w-full max-w-[520px]">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35, ease: EASE }}>
            <Picture />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
